import { mediaDeleteMessage } from '@trilirivos/media-keys';

/**
 * What actually happened, so the copy can describe it instead of guessing.
 *
 * `keyResolved` was a THIRD field here and is gone (TRIL-628 review): an unresolvable key always
 * arrives as a shared `UnresolvableMediaKeyError`, so the flag and the error type were two sources
 * for one fact — the precise shape that produced wrong copy in TRIL-615 and TRIL-622. The error
 * identity is now the only source.
 *
 * Earlier versions took a single `hadImage` boolean and got two rows wrong: an image whose key
 * would not parse was described as "could not be deleted from storage… please try again" when
 * storage was never contacted and no retry could help, and a Formik failure was described as
 * "The block was removed" when it had not been. Both are the same defect as the bug this ticket
 * fixes — telling the user something that is not true about their content.
 */
export interface RemovalOutcome {
  /** The block was an Image block carrying a url, regardless of whether it parsed. */
  hasImage: boolean;
  /** The block itself came out of the editor. */
  blockRemoved: boolean;
}

/**
 * The copy shown when removing a block goes wrong. Pure, so the USER-VISIBLE half of TRIL-615 is
 * testable without mounting a Formik + Redux + Apollo form.
 */
export const imageDeleteMessage = (
  error: unknown,
  outcome: RemovalOutcome,
): string => {
  // The block is still on screen, so nothing about images is the user's problem yet.
  if (!outcome.blockRemoved) {
    return 'That block could not be removed. Please try again.';
  }

  if (!outcome.hasImage) {
    // Defensive, and currently UNREACHABLE via removeBlockWithFeedback: a block with no image has
    // nothing that can fail after `remove()` succeeds, so `blockRemoved && !hasImage` does not
    // occur today. Kept because the alternative is falling through to image copy if a future
    // caller does reach it — blaming storage for a text block would be a guess dressed as a
    // diagnosis. Not claimed as covered: its test pins the mapping, not a live path.
    return 'The block was removed, but something went wrong. Please try again.';
  }

  // The error-specific half comes from @trilirivos/media-keys so web and mobile cannot drift —
  // these strings were byte-identical copies. The "block was removed" prefix stays here because
  // only this surface knows that; mobile's alert has no equivalent claim to make.
  return `The block was removed, but ${lowerFirst(mediaDeleteMessage(error))}`;
};

/** Lower-cases the first character so a shared sentence can follow a clause. */
const lowerFirst = (s: string): string =>
  s ? s[0].toLowerCase() + s.slice(1) : s;
